import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  createLifeOSClient,
  isLifeOSRequestCancelled,
  lifeOSErrorMessage,
  LIFE_OS_LIST_LIMITS,
} from './api';

const LifeOSContext = createContext(null);
const TODAY_CACHE_PREFIX = 'athena.life-os.today.v1';
const TODAY_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const CONTEXT_PREFERENCE_PREFIX = 'athena.life-os.context-mode.v1';

function idleResource(extra = {}) {
  return {
    data: null,
    loading: false,
    error: '',
    fetchedAt: null,
    stale: false,
    source: null,
    ...extra,
  };
}

function browserOnline() {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}

function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Fortaleza';
  } catch {
    return 'America/Fortaleza';
  }
}

function dayInTimezone(date = new Date(), timezone = browserTimezone()) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const byType = Object.fromEntries(parts.map(part => [part.type, part.value]));
    return `${byType.year}-${byType.month}-${byType.day}`;
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

function cachedOwnerId() {
  if (typeof window === 'undefined') return '';
  try {
    return String(JSON.parse(window.localStorage.getItem('athena_user') || 'null')?.id || '').trim();
  } catch {
    return '';
  }
}

function cacheScope(scope) {
  const value = String(scope || cachedOwnerId()).trim();
  return /^[A-Za-z0-9._:-]{1,180}$/.test(value) ? value : '';
}

function savedContextPreference(ownerId) {
  if (typeof window === 'undefined' || !ownerId) return 'auto';
  try {
    const value = window.localStorage.getItem(`${CONTEXT_PREFERENCE_PREFIX}:${ownerId}`);
    return ['auto', 'personal', 'work'].includes(value) ? value : 'auto';
  } catch {
    return 'auto';
  }
}

function automaticContext(surface) {
  // Context is only a relevance lens. It never creates a second account,
  // database, cache namespace or hidden copy of a person's records.
  if (surface === 'workspace') return 'work';
  const hour = new Date().getHours();
  return hour >= 19 || hour < 7 ? 'personal' : 'work';
}

function todayCacheKey(ownerId, date, timezone) {
  const safeOwnerId = cacheScope(ownerId);
  if (!safeOwnerId || !date || !timezone) return '';
  return `${TODAY_CACHE_PREFIX}:${encodeURIComponent(safeOwnerId)}:${date}:${encodeURIComponent(timezone)}`;
}

function readTodayCache(ownerId, date, timezone) {
  if (typeof window === 'undefined') return null;
  const key = todayCacheKey(ownerId, date, timezone);
  if (!key) return null;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || 'null');
    const fetchedAt = typeof parsed?.fetchedAt === 'string' ? parsed.fetchedAt : '';
    const timestamp = Date.parse(fetchedAt);
    if (!parsed?.data || !Number.isFinite(timestamp) || Date.now() - timestamp > TODAY_CACHE_MAX_AGE_MS) {
      if (parsed) window.localStorage.removeItem(key);
      return null;
    }
    return { data: parsed.data, fetchedAt };
  } catch {
    return null;
  }
}

function writeTodayCache(ownerId, date, timezone, data, fetchedAt) {
  if (typeof window === 'undefined') return;
  const key = todayCacheKey(ownerId, date, timezone);
  if (!key || !data) return;
  try {
    window.localStorage.setItem(key, JSON.stringify({ data, fetchedAt }));
  } catch {
    // Local storage can be unavailable or full. The live request remains valid.
  }
}

/** Allows the Auth boundary to clear a user-scoped offline Today snapshot. */
export function clearLifeOSTodayCache(ownerId) {
  if (typeof window === 'undefined') return;
  const scope = cacheScope(ownerId);
  if (!scope) return;
  const prefix = `${TODAY_CACHE_PREFIX}:${encodeURIComponent(scope)}:`;
  try {
    for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(prefix)) window.localStorage.removeItem(key);
    }
  } catch {
    // Cache cleanup is deliberately best effort and must not interrupt logout.
  }
}

function resultList(response, names) {
  for (const name of names) {
    if (Array.isArray(response?.[name])) return response[name];
  }
  return [];
}

function nextCursor(response) {
  return response?.next_cursor || response?.nextCursor || response?.cursor || null;
}

function mergeById(existing, incoming) {
  const seen = new Set();
  return [...existing, ...incoming].filter((item) => {
    const key = item?.id || item?.entity_id || JSON.stringify(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function replaceById(items, item) {
  if (!item?.id) return items;
  const found = items.some(entry => entry?.id === item.id);
  return found ? items.map(entry => (entry?.id === item.id ? item : entry)) : [item, ...items];
}

function requestOptions(params = {}) {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined));
}

/**
 * Shared Life OS state. Data is lazy: mounting the provider alone does not
 * fan out to Today, Inbox or Vault. Consumers ask for their own bounded view.
 */
export function LifeOSProvider({
  children,
  client: providedClient,
  apiBase,
  getToken,
  onUnauthorized,
  cacheOwnerId,
  surface = 'home',
  todayLimit = 18,
  inboxLimit = LIFE_OS_LIST_LIMITS.default,
  vaultLimit = LIFE_OS_LIST_LIMITS.default,
}) {
  const client = useMemo(() => (
    providedClient || createLifeOSClient({ apiBase, getToken, onUnauthorized })
  ), [providedClient, apiBase, getToken, onUnauthorized]);
  const ownerId = cacheScope(cacheOwnerId);
  const normalizedSurface = surface === 'workspace' ? 'workspace' : 'home';
  const [online, setOnline] = useState(browserOnline);
  const [contextPreference, setContextPreferenceState] = useState(() => savedContextPreference(ownerId));
  const [today, setTodayState] = useState(() => idleResource({ date: null, timezone: null }));
  const [inbox, setInboxState] = useState(() => idleResource({ items: [], cursor: null, hasMore: false, params: null }));
  const [vault, setVaultState] = useState(() => idleResource({ items: [], cursor: null, hasMore: false, params: null }));
  const [routines, setRoutinesState] = useState(() => idleResource({ items: [] }));
  const [search, setSearchState] = useState(() => idleResource({ query: '', results: [], semanticSearch: null }));
  const [inboxAction, setInboxAction] = useState({ loading: false, itemId: null, action: null, error: '' });
  const [routineAction, setRoutineAction] = useState({ loading: false, routineId: null, action: null, error: '' });

  const todayRef = useRef(today);
  const inboxRef = useRef(inbox);
  const vaultRef = useRef(vault);
  const routinesRef = useRef(routines);
  const mountedRef = useRef(true);
  const ownerRef = useRef(ownerId);
  const requestsRef = useRef({ today: 0, inbox: 0, vault: 0, routines: 0, search: 0 });

  const commitToday = useCallback((updater) => {
    setTodayState((previous) => {
      const next = typeof updater === 'function' ? updater(previous) : updater;
      todayRef.current = next;
      return next;
    });
  }, []);
  const commitInbox = useCallback((updater) => {
    setInboxState((previous) => {
      const next = typeof updater === 'function' ? updater(previous) : updater;
      inboxRef.current = next;
      return next;
    });
  }, []);
  const commitVault = useCallback((updater) => {
    setVaultState((previous) => {
      const next = typeof updater === 'function' ? updater(previous) : updater;
      vaultRef.current = next;
      return next;
    });
  }, []);
  const commitRoutines = useCallback((updater) => {
    setRoutinesState((previous) => {
      const next = typeof updater === 'function' ? updater(previous) : updater;
      routinesRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (typeof window === 'undefined') {
      return () => { mountedRef.current = false; };
    }
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      mountedRef.current = false;
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // An authenticated surface can stay mounted through a rapid account switch.
  // Never retain an account's in-memory Life OS records for the next owner.
  useEffect(() => {
    if (ownerRef.current === ownerId) return;
    ownerRef.current = ownerId;
    Object.keys(requestsRef.current).forEach((key) => { requestsRef.current[key] += 1; });
    commitToday(idleResource({ date: null, timezone: null }));
    commitInbox(idleResource({ items: [], cursor: null, hasMore: false, params: null }));
    commitVault(idleResource({ items: [], cursor: null, hasMore: false, params: null }));
    commitRoutines(idleResource({ items: [] }));
    setSearchState(idleResource({ query: '', results: [], semanticSearch: null }));
    setContextPreferenceState(savedContextPreference(ownerId));
    setInboxAction({ loading: false, itemId: null, action: null, error: '' });
    setRoutineAction({ loading: false, routineId: null, action: null, error: '' });
  }, [commitInbox, commitRoutines, commitToday, commitVault, ownerId]);

  const setContextPreference = useCallback((value) => {
    const next = ['auto', 'personal', 'work'].includes(value) ? value : 'auto';
    setContextPreferenceState(next);
    if (!ownerId || typeof window === 'undefined') return;
    try { window.localStorage.setItem(`${CONTEXT_PREFERENCE_PREFIX}:${ownerId}`, next); } catch { /* best effort */ }
  }, [ownerId]);
  const effectiveContext = contextPreference === 'auto'
    ? automaticContext(normalizedSurface)
    : contextPreference;
  const trackTechnicalEvent = useCallback((eventName, surface = normalizedSurface) => {
    if (!browserOnline()) return Promise.resolve(null);
    return client.trackTechnicalEvent({ eventName, surface }).catch(() => null);
  }, [client, normalizedSurface]);

  const loadToday = useCallback(async ({ date, timezone, signal, preferCache = true } = {}) => {
    const activeTimezone = timezone || browserTimezone();
    const activeDate = date || dayInTimezone(new Date(), activeTimezone);
    const sequence = ++requestsRef.current.today;
    const cached = preferCache ? readTodayCache(ownerId, activeDate, activeTimezone) : null;

    if (cached && (!todayRef.current.data
      || todayRef.current.date !== activeDate
      || todayRef.current.timezone !== activeTimezone)) {
      commitToday(idleResource({
        data: cached.data,
        date: activeDate,
        timezone: activeTimezone,
        fetchedAt: cached.fetchedAt,
        stale: true,
        source: 'cache',
      }));
    }
    if (!browserOnline()) {
      const current = todayRef.current;
      const fallback = cached || (current.date === activeDate && current.timezone === activeTimezone && current.data
        ? { data: current.data, fetchedAt: current.fetchedAt }
        : null);
      commitToday(idleResource({
        data: fallback?.data || null,
        date: activeDate,
        timezone: activeTimezone,
        fetchedAt: fallback?.fetchedAt || null,
        stale: Boolean(fallback),
        source: fallback ? 'cache' : null,
        error: fallback ? '' : 'Sem conexão e sem dados salvos para este dia.',
      }));
      return fallback?.data || null;
    }
    commitToday(previous => ({
      ...previous,
      date: activeDate,
      timezone: activeTimezone,
      loading: true,
      error: '',
    }));

    try {
      const data = await client.getToday(requestOptions({ date: activeDate, timezone: activeTimezone, signal }));
      if (!mountedRef.current || sequence !== requestsRef.current.today) return data;
      const fetchedAt = new Date().toISOString();
      writeTodayCache(ownerId, activeDate, activeTimezone, data, fetchedAt);
      commitToday(idleResource({
        data,
        date: activeDate,
        timezone: activeTimezone,
        fetchedAt,
        stale: false,
        source: 'network',
      }));
      return data;
    } catch (error) {
      if (!mountedRef.current || sequence !== requestsRef.current.today || isLifeOSRequestCancelled(error)) return null;
      const current = todayRef.current;
      const fallback = cached || (current.date === activeDate && current.timezone === activeTimezone && current.data
        ? { data: current.data, fetchedAt: current.fetchedAt }
        : null);
      commitToday(idleResource({
        data: fallback?.data || null,
        date: activeDate,
        timezone: activeTimezone,
        fetchedAt: fallback?.fetchedAt || null,
        stale: Boolean(fallback),
        source: fallback ? 'cache' : null,
        error: lifeOSErrorMessage(error),
      }));
      return null;
    }
  }, [client, commitToday, ownerId]);

  const loadInbox = useCallback(async ({ append = false, ...options } = {}) => {
    const params = { ...options, limit: options.limit || inboxLimit };
    const requestedCursor = append ? (options.cursor || inboxRef.current.cursor) : options.cursor;
    const sequence = ++requestsRef.current.inbox;
    if (!browserOnline()) {
      commitInbox(previous => ({ ...previous, loading: false, error: 'Sem conexão. A Inbox precisa de conexão para atualizar.' }));
      return null;
    }
    commitInbox(previous => ({ ...previous, loading: true, error: '', params: { ...params, cursor: requestedCursor } }));
    try {
      const data = await client.getInbox(requestOptions({ ...params, cursor: requestedCursor }));
      if (!mountedRef.current || sequence !== requestsRef.current.inbox) return data;
      const incoming = resultList(data, ['items', 'inbox']);
      commitInbox(idleResource({
        data,
        items: append ? mergeById(inboxRef.current.items, incoming) : incoming,
        cursor: nextCursor(data),
        hasMore: Boolean(nextCursor(data)),
        params: { ...params, cursor: requestedCursor },
        fetchedAt: new Date().toISOString(),
        source: 'network',
      }));
      return data;
    } catch (error) {
      if (!mountedRef.current || sequence !== requestsRef.current.inbox || isLifeOSRequestCancelled(error)) return null;
      commitInbox(previous => ({ ...previous, loading: false, error: lifeOSErrorMessage(error) }));
      return null;
    }
  }, [client, commitInbox, inboxLimit]);

  const loadVault = useCallback(async ({ append = false, ...options } = {}) => {
    const params = { ...options, limit: options.limit || vaultLimit };
    const requestedCursor = append ? (options.cursor || vaultRef.current.cursor) : options.cursor;
    const sequence = ++requestsRef.current.vault;
    if (!browserOnline()) {
      commitVault(previous => ({ ...previous, loading: false, error: 'Sem conexão. O Vault precisa de conexão para atualizar.' }));
      return null;
    }
    commitVault(previous => ({ ...previous, loading: true, error: '', params: { ...params, cursor: requestedCursor } }));
    try {
      const data = await client.getVault(requestOptions({ ...params, cursor: requestedCursor }));
      if (!mountedRef.current || sequence !== requestsRef.current.vault) return data;
      const incoming = resultList(data, ['items', 'vault']);
      commitVault(idleResource({
        data,
        items: append ? mergeById(vaultRef.current.items, incoming) : incoming,
        cursor: nextCursor(data),
        hasMore: Boolean(nextCursor(data)),
        params: { ...params, cursor: requestedCursor },
        fetchedAt: new Date().toISOString(),
        source: 'network',
      }));
      if (typeof options.query === 'string' && options.query.trim()) {
        void trackTechnicalEvent('vault_search');
      }
      return data;
    } catch (error) {
      if (!mountedRef.current || sequence !== requestsRef.current.vault || isLifeOSRequestCancelled(error)) return null;
      commitVault(previous => ({ ...previous, loading: false, error: lifeOSErrorMessage(error) }));
      return null;
    }
  }, [client, commitVault, trackTechnicalEvent, vaultLimit]);

  const loadRoutines = useCallback(async (options = {}) => {
    const sequence = ++requestsRef.current.routines;
    if (!browserOnline()) {
      commitRoutines(previous => ({ ...previous, loading: false, error: 'Sem conexão. As rotinas precisam de conexão para atualizar.' }));
      return null;
    }
    commitRoutines(previous => ({ ...previous, loading: true, error: '' }));
    try {
      const data = await client.getRoutines(requestOptions({ ...options, limit: options.limit || inboxLimit }));
      if (!mountedRef.current || sequence !== requestsRef.current.routines) return data;
      commitRoutines(idleResource({
        data,
        items: resultList(data, ['routines', 'items']),
        fetchedAt: new Date().toISOString(),
        source: 'network',
      }));
      return data;
    } catch (error) {
      if (!mountedRef.current || sequence !== requestsRef.current.routines || isLifeOSRequestCancelled(error)) return null;
      commitRoutines(previous => ({ ...previous, loading: false, error: lifeOSErrorMessage(error) }));
      return null;
    }
  }, [client, commitRoutines, inboxLimit]);

  const runSearch = useCallback(async ({ query, q, ...options } = {}) => {
    const searchQuery = String(query ?? q ?? '').trim();
    if (!searchQuery) {
      setSearchState(idleResource({ query: '', results: [], semanticSearch: null }));
      return null;
    }
    const sequence = ++requestsRef.current.search;
    if (!browserOnline()) {
      setSearchState(previous => ({ ...previous, loading: false, error: 'Sem conexão. A busca precisa de conexão para atualizar.', query: searchQuery }));
      return null;
    }
    setSearchState(previous => ({ ...previous, loading: true, error: '', query: searchQuery }));
    try {
      const data = await client.search(requestOptions({ ...options, query: searchQuery }));
      if (!mountedRef.current || sequence !== requestsRef.current.search) return data;
      setSearchState(idleResource({
        data,
        query: searchQuery,
        results: resultList(data, ['results', 'items']),
        semanticSearch: data?.semantic_search || data?.semanticSearch || null,
        fetchedAt: new Date().toISOString(),
        source: 'network',
      }));
      void trackTechnicalEvent('vault_search');
      return data;
    } catch (error) {
      if (!mountedRef.current || sequence !== requestsRef.current.search || isLifeOSRequestCancelled(error)) return null;
      setSearchState(previous => ({ ...previous, loading: false, error: lifeOSErrorMessage(error) }));
      return null;
    }
  }, [client, trackTechnicalEvent]);

  const captureInbox = useCallback(async (payload, options = {}) => {
    setInboxAction({ loading: true, itemId: null, action: 'capture', error: '' });
    try {
      const data = await client.createInbox(payload, options);
      const item = data?.item;
      if (item && mountedRef.current) {
        commitInbox(previous => ({ ...previous, items: replaceById(previous.items, item) }));
      }
      if (item) void trackTechnicalEvent('inbox_capture');
      if (mountedRef.current) setInboxAction({ loading: false, itemId: item?.id || null, action: 'capture', error: '' });
      return data;
    } catch (error) {
      const message = lifeOSErrorMessage(error, 'Não foi possível adicionar esta captura à Inbox.');
      if (mountedRef.current) setInboxAction({ loading: false, itemId: null, action: 'capture', error: message });
      throw error;
    }
  }, [client, commitInbox, trackTechnicalEvent]);

  const reviewInbox = useCallback(async (itemId, action, payload = {}, options = {}) => {
    setInboxAction({ loading: true, itemId, action, error: '' });
    try {
      const data = await client.reviewInbox(itemId, action, payload, options);
      const item = data?.item;
      if (mountedRef.current) {
        commitInbox(previous => {
          const terminal = ['archive', 'discard'].includes(action);
          return {
            ...previous,
            items: terminal ? previous.items.filter(entry => entry?.id !== itemId) : replaceById(previous.items, item),
          };
        });
        setInboxAction({ loading: false, itemId, action, error: '' });
      }
      return data;
    } catch (error) {
      const message = lifeOSErrorMessage(error, 'Não foi possível atualizar este item da Inbox.');
      if (mountedRef.current) setInboxAction({ loading: false, itemId, action, error: message });
      throw error;
    }
  }, [client, commitInbox]);

  const createRoutine = useCallback(async (payload, options = {}) => {
    setRoutineAction({ loading: true, routineId: null, action: 'create', error: '' });
    try {
      const data = await client.createRoutine(payload, options);
      if (data?.routine && mountedRef.current) commitRoutines(previous => ({ ...previous, items: replaceById(previous.items, data.routine) }));
      if (data?.routine) void trackTechnicalEvent('routine_created');
      if (mountedRef.current) setRoutineAction({ loading: false, routineId: data?.routine?.id || null, action: 'create', error: '' });
      return data;
    } catch (error) {
      const message = lifeOSErrorMessage(error, 'Não foi possível criar esta rotina.');
      if (mountedRef.current) setRoutineAction({ loading: false, routineId: null, action: 'create', error: message });
      throw error;
    }
  }, [client, commitRoutines, trackTechnicalEvent]);

  const updateRoutine = useCallback(async (routineId, payload, options = {}) => {
    setRoutineAction({ loading: true, routineId, action: 'update', error: '' });
    try {
      const data = await client.updateRoutine(routineId, payload, options);
      if (data?.routine && mountedRef.current) commitRoutines(previous => ({ ...previous, items: replaceById(previous.items, data.routine) }));
      if (mountedRef.current) setRoutineAction({ loading: false, routineId, action: 'update', error: '' });
      return data;
    } catch (error) {
      const message = lifeOSErrorMessage(error, 'Não foi possível atualizar esta rotina.');
      if (mountedRef.current) setRoutineAction({ loading: false, routineId, action: 'update', error: message });
      throw error;
    }
  }, [client, commitRoutines]);

  const skipRoutine = useCallback(async (routineId, payload = {}, options = {}) => {
    setRoutineAction({ loading: true, routineId, action: 'skip', error: '' });
    try {
      const data = await client.skipRoutine(routineId, payload, options);
      if (data?.routine && mountedRef.current) commitRoutines(previous => ({ ...previous, items: replaceById(previous.items, data.routine) }));
      if (mountedRef.current) setRoutineAction({ loading: false, routineId, action: 'skip', error: '' });
      return data;
    } catch (error) {
      const message = lifeOSErrorMessage(error, 'Não foi possível pular esta rotina.');
      if (mountedRef.current) setRoutineAction({ loading: false, routineId, action: 'skip', error: message });
      throw error;
    }
  }, [client, commitRoutines]);

  const value = useMemo(() => ({
    client,
    online,
    today,
    inbox,
    vault,
    routines,
    search,
    inboxAction,
    routineAction,
    context: {
      preference: contextPreference,
      effective: effectiveContext,
      surface: normalizedSurface,
      setPreference: setContextPreference,
    },
    limits: {
      today: Math.max(1, Number(todayLimit) || 18),
      inbox: inboxLimit,
      vault: vaultLimit,
    },
    loadToday,
    loadInbox,
    loadVault,
    loadRoutines,
    searchLifeOS: runSearch,
    captureInbox,
    reviewInbox,
    createRoutine,
    updateRoutine,
    skipRoutine,
    trackTechnicalEvent,
    clearTodayCache: () => clearLifeOSTodayCache(ownerId),
  }), [
    captureInbox,
    client,
    contextPreference,
    createRoutine,
    effectiveContext,
    inbox,
    inboxAction,
    inboxLimit,
    loadInbox,
    loadRoutines,
    loadToday,
    loadVault,
    online,
    ownerId,
    reviewInbox,
    routineAction,
    routines,
    runSearch,
    search,
    normalizedSurface,
    setContextPreference,
    skipRoutine,
    trackTechnicalEvent,
    today,
    todayLimit,
    updateRoutine,
    vault,
    vaultLimit,
  ]);

  return <LifeOSContext.Provider value={value}>{children}</LifeOSContext.Provider>;
}

export function useLifeOS() {
  const value = useContext(LifeOSContext);
  if (!value) throw new Error('useLifeOS must be used inside LifeOSProvider');
  return value;
}

/** Useful for components that also support controlled, provider-free usage. */
export function useOptionalLifeOS() {
  return useContext(LifeOSContext);
}

export function useLifeOSToday(options) {
  const lifeOS = useLifeOS();
  return { ...lifeOS.today, refresh: () => lifeOS.loadToday(options) };
}

export function useLifeOSInbox(options) {
  const lifeOS = useLifeOS();
  return { ...lifeOS.inbox, refresh: () => lifeOS.loadInbox(options) };
}

export function useLifeOSVault(options) {
  const lifeOS = useLifeOS();
  return { ...lifeOS.vault, refresh: () => lifeOS.loadVault(options) };
}

export const LIFE_OS_DEFAULTS = {
  todayLimit: 18,
  listLimit: LIFE_OS_LIST_LIMITS.default,
};
